import {
  Flex,
  Box,
  Text,
  IconButton,
  HStack,
  useToast,
  Input,
  Tooltip,
  Icon,
  Select,
  SimpleGrid,
  InputLeftElement,
  InputGroup,
  Heading,
  Spinner,
} from "@chakra-ui/react";
import { FaTh, FaListUl, FaFileExcel, FaFileSignature } from "react-icons/fa";
import { FiInfo } from "react-icons/fi";
import { useEffect, useState } from "react";
import { getProposalsV2 } from "@/services/proposal.service";
import { useQuery } from "@tanstack/react-query";
import ProposalCard from "../ProposalCard";
import ProposalNewTable from "../ProposalNewTable";
import CustomDatePicker from "@/components/CustomDatePicker";
import { proposalStatusLabels } from "@/utils/getProposalStatusLabel";
import { ProposalStatus } from "../ProposalTable/proposalStatusRenderer";
import { userPermissions } from "@/contexts/userPermissions";
import { useEnsureAuthenticated } from "@/contexts/ensureAuthenticated";
import { PermissionEnum } from "@/utils/permissionEnum";
import { Corban } from "@/models/corban.model";
import { getCorbansByCreditPromoterIdPaginated } from "@/services/corban.service";
import { CiSearch } from "react-icons/ci";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import { format } from "date-fns";
import { downloadProposalExcel } from "@/services/proposalExcel.service";

export default function ProposalsPage() {
  const toast = useToast();
  const [selectedView, setSelectedView] = useState<"grid" | "list">("grid");
  const [activeTab, setActiveTab] = useState<
    "emAndamento" | "pago" | "cancelado"
  >("emAndamento");
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const backgroundStyle = useConditionalBackground();

  const { user } = useEnsureAuthenticated();
  const permissions = userPermissions(user);
  const isPromoter = permissions.some(
    (permissions) => permissions == PermissionEnum.GRANATECH
  );
 const [selectedCorban, setSelectedCorban] = useState<
    string | number | undefined
  >(undefined);
  const [corbans, setCorbans] = useState<Corban[]>([]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("");

  const [loadExcel, setLoadExcel] = useState<boolean>(false);

  useEffect(() => {
    fetchCorbans(user?.creditPromoterId!);
  }, [user]);

  const fetchCorbans = async (creditPromoterId: number) => {
    if (!creditPromoterId) return null;
    try {
      const response = await getCorbansByCreditPromoterIdPaginated(
        1,
        10,
        creditPromoterId
      );
      setCorbans(response.data);
    } catch (error) {
      console.error("Erro ao obter a lista de Corbans:", error);
      toast({
        title: "Erro ao obter a lista de Corbans.",
        description: "Tente novamente mais tarde.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  type ProposalStatusType =
    (typeof ProposalStatus)[keyof typeof ProposalStatus];

  const payedStatuses: ProposalStatusType[] = [ProposalStatus.PAYED];
  const canceledStatuses: ProposalStatusType[] = [
    ProposalStatus.CANCELED,
    ProposalStatus.CANCELED_BY_CLIENT,
    ProposalStatus.REPROVED,
  ];
  const activeStatuses: ProposalStatusType[] = Object.values(
    ProposalStatus
  ).filter(
    (status) =>
      !payedStatuses.includes(status as ProposalStatusType) &&
      !canceledStatuses.includes(status as ProposalStatusType) &&
      status !== ProposalStatus.PENDING_CONFIRMATION
  );

  const fetchProposalsData = async () => {
    let statuses;
    switch (activeTab) {
      case "pago":
        statuses = payedStatuses;
        break;
      case "cancelado":
        statuses = canceledStatuses;
        break;
      case "emAndamento":
        statuses = activeStatuses;
        break;
    }

    if (statusFilter) statuses = [statusFilter];

    const startDate = dateRange[0]
      ? new Date(dateRange[0]).toISOString()
      : null;
    const endDate = dateRange[1] ? new Date(dateRange[1]).toISOString() : null;

    return await getProposalsV2({
      page,
      pageSize,
      statuses,
      sortBy,
      sortDirection,
      searchTerm,
      startDate,
      endDate,
      corbanId: selectedCorban
    });
  };

  const staleSeconds = 4;

  const { data, isLoading } = useQuery({
    queryKey: [
      "proposals",
      page,
      pageSize,
      activeTab,
      sortBy,
      sortDirection,
      searchTerm,
      statusFilter,
      dateRange,
    ],
    queryFn: fetchProposalsData,
    staleTime: staleSeconds * 1000,
    refetchInterval: staleSeconds * 1000,
  });

  const proposals = data?.data || [];
  const totalPages = data?.totalPages || 1;

  const handleDownload = async () => {
    setLoadExcel(true);
    try {
      const statusMapping = {
        emAndamento: "inProgress",
        pago: "payed",
        cancelado: "cancelled",
      };
      const mappedStatus = statusMapping[activeTab];

      const startDateValue = dateRange[0]
        ? new Date(dateRange[0]).toISOString()
        : null;
      const endDateValue = dateRange[1]
        ? new Date(dateRange[1]).toISOString()
        : null;

      const blob = await downloadProposalExcel({
        status: mappedStatus,
        searchTerm,
        startDate: startDateValue,
        endDate: endDateValue,
      });

      let filename = `Propostas_${activeTab}.xlsx`;
      if (dateRange[0] && dateRange[1]) {
        const start = format(dateRange[0], "dd/MM/yyyy");
        const end = format(dateRange[1], "dd/MM/yyyy");
        const dateSegment = start === end ? start : `${start}-${end}`;
        filename = `Propostas_${activeTab}_${dateSegment}.xlsx`;
      }

      const url = window.URL.createObjectURL(
        new Blob([blob], { type: "application/octet-stream" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: "Arquivo Excel baixado com sucesso.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Erro ao gerar o Excel:", error);
      toast({
        title: "Erro ao gerar o arquivo Excel",
        description: "Tente novamente mais tarde.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadExcel(false);
    }
  };

  const getStatusOptions = () => {
    if (activeTab === "pago") {
      return [
        {
          value: ProposalStatus.PAYED,
          label: proposalStatusLabels[ProposalStatus.PAYED],
        },
      ];
    }
    if (activeTab === "cancelado") {
      return [
        {
          value: ProposalStatus.CANCELED,
          label: proposalStatusLabels[ProposalStatus.CANCELED],
        },
        {
          value: ProposalStatus.CANCELED_BY_CLIENT,
          label: proposalStatusLabels[ProposalStatus.CANCELED_BY_CLIENT],
        },
        {
          value: ProposalStatus.REPROVED,
          label: proposalStatusLabels[ProposalStatus.REPROVED],
        },
      ];
    }
    return Object.entries(proposalStatusLabels)
      .filter(
        ([status]) =>
          status !== ProposalStatus.PAYED &&
          status !== ProposalStatus.CANCELED &&
          status !== ProposalStatus.CANCELED_BY_CLIENT &&
          status !== ProposalStatus.REPROVED
      )
      .map(([status, label]) => ({ value: status, label }));
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  return (
    <Box w={"100%"} padding={{ base: "2", md: "4" }}>
      <Box
        {...backgroundStyle}
        borderRadius="md"
        p={{ base: 4, md: 6 }}
        mb={2}
        color="white"
        boxShadow="md"
        h="auto"
      >
        <Box mb={{ base: 0, md: -10 }}>
          <Flex
            justify={{ base: "center", md: "flex-start" }}
            alignItems={"center"}
            gap={4}
          >
            <FaFileSignature size="1.75rem" />
            <Text
              fontSize={{ base: "2xl", md: "4xl" }}
              maxW={{ base: "100%", md: "80%", lg: "70%" }}
              fontWeight={"bold"}
            >
              Propostas
            </Text>
          </Flex>
        </Box>
        <HStack
          spacing={{ base: 4, md: 6, lg: 8 }}
          justify="space-evenly"
          mb={{ base: 4, md: 6 }}
          mx="auto"
          flexWrap="wrap"
          maxW={{ base: "100%", md: "80%", lg: "70%" }}
        >
          <Box
            textAlign="center"
            position="relative"
            cursor="pointer"
            onClick={() => {
              setActiveTab("emAndamento");
              setPage(1);
            }}
            mb={{ base: 2, md: 0 }}
          >
            <Text
              fontSize={{ base: "md", md: "md", lg: "xl" }}
              fontWeight="bold"
              color={activeTab === "emAndamento" ? "white" : "whiteAlpha.800"}
              transition="color 0.3s ease"
              fontFamily="Lexend Deca"
            >
              EM ANDAMENTO
            </Text>
            {activeTab === "emAndamento" && (
              <Box
                position="absolute"
                bottom="-0.25rem"
                left="50%"
                transform="translateX(-50%)"
                height="0.25rem"
                width="2.5rem"
                bgColor="white"
                transition="width 0.3s ease, background-color 0.3s ease"
              />
            )}
          </Box>

          <Box
            textAlign="center"
            position="relative"
            cursor="pointer"
            onClick={() => {
              setActiveTab("pago");
              setPage(1);
            }}
            mb={{ base: 2, md: 0 }}
          >
            <Text
              fontSize={{ base: "md", md: "md", lg: "xl" }}
              fontWeight="bold"
              color={activeTab === "pago" ? "white" : "whiteAlpha.800"}
              transition="color 0.3s ease"
              fontFamily="Lexend Deca"
            >
              PAGO
            </Text>
            {activeTab === "pago" && (
              <Box
                position="absolute"
                bottom="-0.25rem"
                left="50%"
                transform="translateX(-50%)"
                height="0.25rem"
                width="2.5rem"
                bgColor="white"
                transition="width 0.3s ease, background-color 0.3s ease"
              />
            )}
          </Box>
          <Box
            textAlign="center"
            position="relative"
            cursor="pointer"
            onClick={() => {
              setActiveTab("cancelado");
              setPage(1);
            }}
            mb={{ base: 2, md: 0 }}
            px={{ base: 2, md: 4 }}
          >
            <Text
              fontSize={{ base: "md", md: "md", lg: "xl" }}
              fontWeight="bold"
              color={activeTab === "cancelado" ? "white" : "whiteAlpha.800"}
              transition="color 0.3s ease"
              fontFamily="Lexend Deca"
            >
              CANCELADO
            </Text>
            {activeTab === "cancelado" && (
              <Box
                position="absolute"
                bottom="-0.25rem"
                left="50%"
                transform="translateX(-50%)"
                height="0.25rem"
                width={{ base: "1.25rem", sm: "1.875rem", md: "2.5rem" }}
                bgColor="white"
                transition="width 0.3s ease, background-color 0.3s ease"
              />
            )}
          </Box>
        </HStack>
        <Flex
          alignItems="center"
          mt={9}
          flexWrap="wrap"
          justifyContent="space-between"
        >
          <HStack
            spacing={4}
            mb={{ base: 4, md: 0 }}
            flexDirection={{ base: "column", md: "column", lg: "row" }}
            alignItems={{ base: "center", md: "center" }}
            width="100%"
          >
            <Flex w={{ base: "100%", md: "70%", lg: "20%" }}>
              <InputGroup>
                <InputLeftElement mt={{ base: 2, md: 1 }} pointerEvents="none">
                  <CiSearch color="black" size="2rem" />
                </InputLeftElement>
                <Input
                  size={"md"}
                  height={{ base: "3.5rem", md: "3rem" }}
                  placeholder="Pesquisar Propostas..."
                  variant="filled"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  type="text"
                  fontSize={{ base: 10, sm: 10, md: 10, lg: 10, xl: 15 }}
                  bg={"white"}
                  maxLength={30}
                  color="black"
                  _focus={{ bg: "white" }}
                  _hover={{ bg: "white" }}
                  fontFamily="Lexend Deca"
                />
              </InputGroup>
            </Flex>
            <Tooltip
              label={
                <>
                  Filtre por:
                  <br />- Número da Proposta
                  <br />- Nome do Cliente
                  <br />- CPF do Cliente
                  <br />- Vendedor
                </>
              }
              fontSize="lg"
              placement="top"
            >
              <Box mt={{ base: 0, md: 2 }}>
                <Icon as={FiInfo} boxSize={{ base: 0, md: 7 }} color="white" />
              </Box>
            </Tooltip>

            <CustomDatePicker
              dateRange={dateRange}
              onChange={(dates) => setDateRange(dates)}
            />
            <Box
              width={{ base: "100%", md: "70%", lg: "20%" }}
              mt={{ base: 5, md: 0 }}
              flex={1}
            >
              <Tooltip label="FILTRAR STATUS" fontSize="md" placement="bottom">
                <Select
                  placeholder="-------"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  bg="white"
                  height={{ base: "3.5rem", md: "3rem" }}
                  color="black"
                  fontFamily="Lexend Deca"
                  mt={{ base: 5, md: 0 }}
                  width={"100%"}
                  fontSize={{ base: "0.8rem", md: "1rem" }}
                >
                  {getStatusOptions().map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </Tooltip>
            </Box>
            {user && isPromoter && (
              <Box
                width={{ base: "100%", md: "70%", lg: "20%" }}
                mt={{ base: 5, md: 0 }}
                flex={1}
              >
                <Tooltip label="FILTRAR CORBAN" fontSize="md" placement="bottom">
                  <Select
                    placeholder="-------"
                    value={selectedCorban || ""}
                    onChange={(e) => setSelectedCorban(e.target.value)}
                    bg="white"
                    height={{ base: "3.5rem", md: "3rem" }}
                    color="black"
                    fontFamily="Lexend Deca"
                    mt={{ base: 5, md: 0 }}
                    display={user.creditPromoterId ? "block" : "none"}
                    width={"100%"}
                    fontSize={{ base: "0.8rem", md: "1rem" }}
                  >
                    {corbans.map((corban) => (
                      <option key={corban.id} value={corban.id}>
                        {corban.businessName}
                      </option>
                    ))}
                  </Select>
                </Tooltip>
              </Box>
            )}
            <HStack spacing={2} ml={{ base: "0", md: "auto" }}>
              {loadExcel ? (
                <Spinner
                  size="md"
                  color="primary"
                  thickness="4px"
                  speed="0.65s"
                />
              ) : (
                <IconButton
                  icon={<FaFileExcel />}
                  aria-label="Download Excel"
                  onClick={handleDownload}
                  colorScheme="green"
                />
              )}
              <IconButton
                icon={<FaTh />}
                aria-label="List View"
                onClick={() => setSelectedView("grid")}
                colorScheme="whiteAlpha"
                variant={selectedView === "list" ? "solid" : "outline"}
              />
              <IconButton
                icon={<FaListUl />}
                aria-label="Grid View"
                onClick={() => setSelectedView("list")}
                colorScheme="whiteAlpha"
                variant={selectedView === "grid" ? "solid" : "outline"}
              />
            </HStack>
          </HStack>
        </Flex>
      </Box>
      {isLoading ? (
        <Flex justifyContent="center" mt={100} mb={2} alignItems="center">
          <Spinner size="xl" color="gray.600" />
        </Flex>
      ) : proposals.length ? (
        <Flex justifyContent="center" mt={2} mb={2} alignItems="center">
          <IconButton
            icon={<ChevronLeftIcon />}
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            isDisabled={page === 1}
            mr={2}
            aria-label="Página Anterior"
            size={{ base: "sm", md: "sm" }}
          />
          <Text mx={4}>
            Página {page} de {totalPages}
          </Text>
          <IconButton
            icon={<ChevronRightIcon />}
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            isDisabled={page === totalPages}
            ml={2}
            aria-label="Próxima Página"
            size={{ base: "sm", md: "sm" }}
          />
          <Flex alignItems="center" ml={4}>
            <Text mr={2}>Itens por página:</Text>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              width="fit-content"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Select>
          </Flex>
        </Flex>
      ) : (
        <Heading mt={100} textAlign={"center"} color="gray.600">
          Nenhuma Proposta Encontrada
        </Heading>
      )}

      {selectedView === "grid" ? (
        <SimpleGrid
          templateColumns={{
            base: "1fr",
            sm: "repeat(1,1fr)",
            md: "repeat(2, 1fr)",
            lg: "repeat(2, 1fr)",
            xl: "repeat(3, 1fr)",
            "2xl": "repeat(4,1fr)",
          }}
          spacing={{ base: 2, sm: 3, md: 4 }}
        >
          {proposals.map((proposal, index) => (
            <ProposalCard key={index} proposal={proposal} />
          ))}
        </SimpleGrid>
      ) : (
        <ProposalNewTable
          setSortBy={setSortBy}
          setSortDirection={setSortDirection}
          sortBy={sortBy}
          sortDirection={sortDirection}
          proposals={proposals}
        />
      )}
      {proposals.length ? (
        <Flex justifyContent="center" mt={2} mb={2} alignItems="center">
          <IconButton
            icon={<ChevronLeftIcon />}
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            isDisabled={page === 1}
            mr={2}
            aria-label="Página Anterior"
            size={{ base: "sm", md: "sm" }}
          />
          <Text mx={4}>
            Página {page} de {totalPages}
          </Text>
          <IconButton
            icon={<ChevronRightIcon />}
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            isDisabled={page === totalPages}
            ml={2}
            aria-label="Próxima Página"
            size={{ base: "sm", md: "sm" }}
          />

          <Flex alignItems="center" ml={4}>
            <Text mr={2}>Itens por página:</Text>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              width="fit-content"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Select>
          </Flex>
        </Flex>
      ) : null}
    </Box>
  );
}
